import { create } from 'zustand';
import { crearPersonajeInicial, type Character, type StatKey } from '@/types';
import { cargar, guardar } from '@/engine/persistence';
import { useMetaStore } from './metaStore';

interface CharacterStoreState {
  personaje: Character;
  cargado: boolean;
  setNombre: (nombre: string) => void;
  modificarStat: (stat: StatKey, delta: number) => void;
  ganarXp: (cantidad: number) => void;
  reiniciar: () => void;
  cargarDesdeStorage: () => void;
  persistir: () => void;
}

export const useCharacterStore = create<CharacterStoreState>((set, get) => ({
  personaje: crearPersonajeInicial(),
  cargado: false,

  setNombre: (nombre) => {
    set((s) => ({ personaje: { ...s.personaje, nombre } }));
    get().persistir();
  },

  modificarStat: (stat, delta) => {
    set((s) => {
      const actual = s.personaje.stats[stat] + delta;
      const clamp =
        stat === 'vida'
          ? Math.max(0, Math.min(actual, s.personaje.stats.vidaMax))
          : stat === 'mana'
            ? Math.max(0, Math.min(actual, s.personaje.stats.manaMax))
            : Math.max(0, actual);
      return {
        personaje: {
          ...s.personaje,
          stats: { ...s.personaje.stats, [stat]: clamp },
        },
      };
    });
    get().persistir();

    // Si la vida llegó a cero, disparar Game Over (salvo si hay combate activo
    // — ese caso lo maneja la CombatScreen).
    if (stat === 'vida' && get().personaje.stats.vida === 0) {
      useMetaStore.getState().dispararGameOverMuerte();
    }
  },

  ganarXp: (cantidad) => {
    set((s) => {
      const xp = s.personaje.xp + cantidad;
      if (xp >= s.personaje.xpParaSiguienteNivel) {
        return {
          personaje: {
            ...s.personaje,
            xp: xp - s.personaje.xpParaSiguienteNivel,
            nivel: s.personaje.nivel + 1,
            xpParaSiguienteNivel: Math.floor(s.personaje.xpParaSiguienteNivel * 1.5),
            puntosSkillDisponibles: s.personaje.puntosSkillDisponibles + 1,
          },
        };
      }
      return { personaje: { ...s.personaje, xp } };
    });
    get().persistir();
  },

  reiniciar: () => {
    set({ personaje: crearPersonajeInicial(), cargado: true });
    get().persistir();
  },

  cargarDesdeStorage: () => {
    const r = cargar<Character>('character');
    if (r.status === 'ok') {
      set({ personaje: r.valor, cargado: true });
    } else {
      set({ cargado: true });
    }
  },

  persistir: () => {
    guardar('character', get().personaje);
  },
}));
